// 탭 끌기 — 창 밖에 놓으면 새 창으로 분리, 다른 세션 창에 놓으면 합치기(판정·전달은 bridge.ts),
// 자기 창 탭 줄 안에서는 순서 바꾸기(미리보기·확정은 SessionTabs — 이 훅은 포인터 흐름만 알린다).
//
// 💡 HTML5 드래그앤드롭(draggable)을 쓰지 않는 이유:
//   - Tauri(Windows)는 창의 파일 드롭 처리기가 HTML5 DnD 이벤트를 가로챈다(창마다 설정을 꺼야 한다).
//   - 창 밖에서 놓은 위치를 얻기 어렵다(`dragend` 좌표가 브라우저마다 다르다).
//   포인터 캡처(`setPointerCapture`)는 버튼을 누른 채 창 밖으로 나가도 이벤트가 계속 와서 두 문제가 없다.
import { useCallback, useRef, useState } from 'react';
import type { PointerEvent as ReactPointerEvent } from 'react';
import type { DropPoint } from './types';

/** 이만큼 움직여야 "끌기"로 본다(그 전에 떼면 평범한 클릭 = 탭 선택). */
const DRAG_THRESHOLD_PX = 8;

/** 끄는 동안 포인터를 따라다니는 탭 그림자(창 안에서만 보인다 — 창 밖은 앱의 미리보기 창이 맡는다). */
export interface TabGhost {
  id: string;
  x: number;
  y: number;
}

export interface TabDragHandlers {
  /** 끌어서 놓았을 때(임계값을 넘겨 움직인 뒤 뗐을 때만). 없으면 끌기를 켜지 않는다. */
  onDrop?: (id: string, pt: DropPoint) => void;
  /** 끌기가 시작될 때(임계값을 넘은 순간 1회) — 창 밖 미리보기를 켠다. */
  onDragStart?: (id: string) => void;
  /** 끄는 동안 포인터가 움직일 때마다(창 기준 좌표) — 탭 순서 미리보기(다른 탭이 비켜 주기)를 갱신한다. */
  onDragMove?: (id: string, clientX: number, clientY: number) => void;
  /**
   * 끌기가 끝날 때(놓기·취소 모두, `onDrop` 보다 먼저) — 미리보기를 끈다.
   * @param dropped 놓아서 끝났으면 true(곧 `onDrop` 이 온다), 캡처를 잃거나 취소됐으면 false(놓지 않은 것)
   */
  onDragEnd?: (dropped: boolean) => void;
}

interface DragState {
  id: string;
  pointerId: number;
  x0: number;
  y0: number;
  dragging: boolean;
}

/** @returns 탭별 포인터 처리기, 그림자 상태, 클릭 억제 판정(끌기 끝의 클릭이 탭 선택으로 새지 않게) */
export function useTabDrag({ onDrop, onDragStart, onDragMove, onDragEnd }: TabDragHandlers) {
  const drag = useRef<DragState | null>(null);
  const suppressClick = useRef(false);
  const [ghost, setGhost] = useState<TabGhost | null>(null);

  /**
   * 끌기 상태를 비운다. 실제로 끌던 중이었으면 끝 콜백을 부른다(시작 콜백과 짝이 맞게).
   * @param dropped 놓아서 끝났는지(취소·캡처 상실이면 false)
   */
  const finish = useCallback(
    (dropped: boolean) => {
      const d = drag.current;
      drag.current = null;
      setGhost(null);
      if (d?.dragging) {
        onDragEnd?.(dropped);
      }
      return d;
    },
    [onDragEnd],
  );

  const handlersFor = useCallback(
    (id: string) => ({
      onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
        suppressClick.current = false;
        if (!onDrop || e.button !== 0 || (e.target as HTMLElement).closest('.tab-close')) {
          return;
        }
        e.currentTarget.setPointerCapture(e.pointerId);
        drag.current = { id, pointerId: e.pointerId, x0: e.clientX, y0: e.clientY, dragging: false };
      },
      onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
        const d = drag.current;
        if (!d || d.pointerId !== e.pointerId) {
          return;
        }
        if (!d.dragging) {
          if (Math.hypot(e.clientX - d.x0, e.clientY - d.y0) < DRAG_THRESHOLD_PX) {
            return;
          }
          d.dragging = true;
          onDragStart?.(id);
        }
        setGhost({ id, x: e.clientX, y: e.clientY });
        onDragMove?.(id, e.clientX, e.clientY);
      },
      onPointerUp: (e: ReactPointerEvent<HTMLElement>) => {
        const d = drag.current;
        if (!d || d.pointerId !== e.pointerId) {
          return;
        }
        finish(true);
        if (d.dragging) {
          suppressClick.current = true;
          onDrop?.(id, { screenX: e.screenX, screenY: e.screenY, clientX: e.clientX, clientY: e.clientY });
        }
      },
      // 캡처를 잃거나(창 전환 등) 취소되면 놓지 않은 것으로 본다. 정상 종료는 pointerup 이 먼저 finish 한다.
      onPointerCancel: () => void finish(false),
      onLostPointerCapture: () => void finish(false),
    }),
    [onDrop, onDragStart, onDragMove, finish],
  );

  /** 클릭 처리기 첫 줄에서 부른다 — 방금 끌기로 끝난 클릭이면 true(무시할 것). */
  const consumeClick = useCallback(() => {
    const s = suppressClick.current;
    suppressClick.current = false;
    return s;
  }, []);

  return { handlersFor, ghost, consumeClick };
}
